import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserIcon, MailIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, ChevronDownIcon } from 'lucide-react';
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/VPeOQro95PyIFiaEYd4a/webhook-trigger/a7ce68e9-61b2-46de-b2d7-cfb8323eb83d';
const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/YOUR-GROUP-LINK-HERE';
const COUNTRY_CODES = [{
  code: '+1',
  flag: '🇺🇸',
  label: 'US'
}, {
  code: '+52',
  flag: '🇲🇽',
  label: 'MX'
}, {
  code: '+57',
  flag: '🇨🇴',
  label: 'CO'
}, {
  code: '+51',
  flag: '🇵🇪',
  label: 'PE'
}, {
  code: '+56',
  flag: '🇨🇱',
  label: 'CL'
}, {
  code: '+54',
  flag: '🇦🇷',
  label: 'AR'
}, {
  code: '+593',
  flag: '🇪🇨',
  label: 'EC'
}, {
  code: '+58',
  flag: '🇻🇪',
  label: 'VE'
}, {
  code: '+507',
  flag: '🇵🇦',
  label: 'PA'
}, {
  code: '+506',
  flag: '🇨🇷',
  label: 'CR'
}, {
  code: '+502',
  flag: '🇬🇹',
  label: 'GT'
}, {
  code: '+503',
  flag: '🇸🇻',
  label: 'SV'
}, {
  code: '+504',
  flag: '🇭🇳',
  label: 'HN'
}, {
  code: '+505',
  flag: '🇳🇮',
  label: 'NI'
}, {
  code: '+591',
  flag: '🇧🇴',
  label: 'BO'
}, {
  code: '+595',
  flag: '🇵🇾',
  label: 'PY'
}, {
  code: '+598',
  flag: '🇺🇾',
  label: 'UY'
}, {
  code: '+34',
  flag: '🇪🇸',
  label: 'ES'
}];
interface FormState {
  name: string;
  email: string;
  countryCode: string;
  whatsapp: string;
}
export function LeadCaptureForm() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    countryCode: '+1',
    whatsapp: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email || !form.whatsapp) return;
    setLoading(true);
    setError(null);
    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        mode: 'no-cors',
        body: new URLSearchParams({
          name: form.name,
          email: form.email,
          phone: form.whatsapp.replace(/\D/g, ''),
          phone_country_code: form.countryCode
        })
      });
      // no-cors means we can't read the response, so we assume success
      // Redirect to thank-you page
      window.location.hash = '#/thank-you';
    } catch (err) {
      setError('Hubo un problema al enviar tu registro. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.6,
    ease: 'easeOut',
    delay: 0.3
  }} className="glass-cyan" style={{
    padding: '24px 20px',
    width: '100%',
    maxWidth: '440px'
  }} data-id="element-3">
      <AnimatePresence mode="wait" data-id="element-4">
        {submitted ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} style={{
        textAlign: 'center',
        padding: '24px 0'
      }} data-id="element-5">
            <div style={{
          marginBottom: '16px'
        }} data-id="element-6">
              <CheckCircleIcon size={56} color="var(--color-cyan)" style={{
            margin: '0 auto'
          }} data-id="element-7" />
            </div>
            <h3 style={{
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 900,
          fontSize: '22px',
          color: 'white',
          marginBottom: '8px'
        }} data-id="element-8">
              ¡Tu lugar está reservado! 🎉
            </h3>
            <p style={{
          color: '#a0a0b0',
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          lineHeight: 1.6
        }} data-id="element-9">
              Revisa tu correo electrónico. Te enviaremos los detalles del
              masterclass en los próximos minutos.
            </p>
            <a href={WHATSAPP_GROUP_URL} target="_blank" rel="noopener noreferrer" style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          marginTop: '20px',
          width: '100%',
          padding: '14px 24px',
          background: '#25D366',
          borderRadius: '12px',
          border: 'none',
          color: 'white',
          fontSize: '15px',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 700,
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'background 0.2s ease'
        }} onMouseEnter={e => e.currentTarget.style.background = '#1ebe5b'} onMouseLeave={e => e.currentTarget.style.background = '#25D366'} data-id="element-10">
              <span style={{
            fontSize: '20px'
          }} data-id="element-11">
                💬
              </span>
              ÚNETE AL GRUPO DE WHATSAPP →
            </a>
          </motion.div> : <motion.div key="form" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-12">
            {/* Header */}
            <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }} data-id="element-13">
              <div style={{
            display: 'inline-block',
            background: 'rgba(24,163,159,0.1)',
            border: '1px solid rgba(24,163,159,0.35)',
            borderRadius: '8px',
            padding: '4px 12px',
            marginBottom: '10px'
          }} data-id="element-14">
                <span style={{
              color: 'var(--color-teal)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              fontFamily: 'Poppins, sans-serif'
            }} data-id="element-15">
                  RESERVA TU LUGAR GRATIS
                </span>
              </div>
              <h3 style={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 900,
            fontSize: '20px',
            color: 'white',
            marginBottom: '6px',
            lineHeight: 1.3
          }} data-id="element-16">
                Accede al Masterclass en Vivo
              </h3>
              <p style={{
            color: '#a0a0b0',
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-17">
                Aprende el sistema completo + grabación exclusiva
              </p>
            </div>

            {/* Divider */}
            <div style={{
          height: '1px',
          background: 'rgba(24,163,159,0.2)',
          marginBottom: '20px'
        }} data-id="element-18" />

            {/* Form */}
            <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }} data-id="element-19">
              {/* Name */}
              <div style={{
            position: 'relative'
          }} data-id="element-20">
                <div style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1
            }} data-id="element-21">
                  <UserIcon size={16} color="#6b6b80" data-id="element-22" />
                </div>
                <input className="input-glass" type="text" placeholder="Tu nombre completo" value={form.name} onChange={e => setForm(f => ({
              ...f,
              name: e.target.value
            }))} required data-id="element-23" />
              </div>

              {/* Email */}
              <div style={{
            position: 'relative'
          }} data-id="element-24">
                <div style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1
            }} data-id="element-25">
                  <MailIcon size={16} color="#6b6b80" data-id="element-26" />
                </div>
                <input className="input-glass" type="email" placeholder="tu@email.com" value={form.email} onChange={e => setForm(f => ({
              ...f,
              email: e.target.value
            }))} required data-id="element-27" />
              </div>

              {/* WhatsApp with Country Code */}
              <div style={{
            display: 'flex',
            gap: '8px'
          }} data-id="element-28">
                {/* Country Code Dropdown */}
                <div style={{
              position: 'relative',
              flexShrink: 0
            }} data-id="element-29">
                  <select value={form.countryCode} onChange={e => setForm(f => ({
                ...f,
                countryCode: e.target.value
              }))} style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '12px 30px 12px 12px',
                color: 'white',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                outline: 'none',
                width: '105px',
                height: '100%',
                transition: 'border-color 0.2s'
              }} onFocus={e => e.currentTarget.style.borderColor = 'rgba(24,163,159,0.5)'} onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'} data-id="element-30">
                    {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} style={{
                  background: '#1a1a24',
                  color: 'white'
                }} data-id="element-31">
                        {c.flag} {c.code}
                      </option>)}
                  </select>
                  <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }} data-id="element-32">
                    <ChevronDownIcon size={14} color="#6b6b80" data-id="element-33" />
                  </div>
                </div>

                {/* Phone Number Input */}
                <div style={{
              position: 'relative',
              flex: 1
            }} data-id="element-34">
                  <div style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }} data-id="element-35">
                    <PhoneIcon size={16} color="#6b6b80" data-id="element-36" />
                  </div>
                  <input className="input-glass" type="tel" placeholder="(555) 000-0000" value={form.whatsapp} onChange={e => setForm(f => ({
                ...f,
                whatsapp: e.target.value
              }))} required style={{
                width: '100%'
              }} data-id="element-37" />
                </div>
              </div>

              {/* CTA Button */}
              <motion.button type="submit" className="btn-cta" disabled={loading} animate={loading ? {} : {
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            marginTop: '4px',
            opacity: loading ? 0.7 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }} data-id="element-38">
                {loading ? '⏳ Enviando...' : '🚀 QUIERO MI LUGAR GRATIS →'}
              </motion.button>

              {/* Error message */}
              {error && <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            background: 'rgba(242,42,114,0.1)',
            border: '1px solid rgba(242,42,114,0.3)',
            borderRadius: '10px',
            padding: '10px 14px',
            marginTop: '4px'
          }} data-id="element-39">
                  <AlertCircleIcon size={16} color="var(--color-pink)" style={{
              flexShrink: 0,
              marginTop: '1px'
            }} data-id="element-40" />
                  <span style={{
              color: 'var(--color-pink)',
              fontSize: '13px',
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1.5
            }} data-id="element-41">
                    {error}
                  </span>
                </div>}
            </form>

            {/* Social proof mini */}
            <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginTop: '14px'
        }} data-id="element-42">
              <div style={{
            display: 'flex'
          }} data-id="element-43">
                {['MR', 'CL', 'AP', 'JG'].map((initials, i) => <div key={initials} style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: ['#18a39f', '#f22a72', '#ffe222', '#18a39f'][i],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 700,
              color: i === 2 ? '#0a0a0f' : 'white',
              marginLeft: i === 0 ? 0 : '-8px',
              border: '2px solid #0a0a0f',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-44">
                    {initials}
                  </div>)}
              </div>
              <span style={{
            color: '#a0a0b0',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-45">
                <strong style={{
              color: 'white'
            }} data-id="element-46">
                  2,400+
                </strong>{' '}
                ya registrados
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}